import { createElement, forwardRef } from 'react';
import type { ElementType, ReactNode, Ref } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { revealVariants, useRevealProps } from './motion';
import type { RevealVariantName } from './motion';

export interface RevealProps {
  /** Element/component to render. Defaults to 'div'. */
  as?: ElementType;
  /** Reveal delay in seconds (ignored under reduced motion). */
  delay?: number;
  /** Named variant from motion.ts. Defaults to 'fadeUp'. */
  variant?: RevealVariantName;
  className?: string;
  children?: ReactNode;
}

/**
 * Scroll-reveal wrapper. Fades + lifts its children into view once,
 * using the shared variants. Under prefers-reduced-motion it renders a
 * plain element already in its final state (no transform / transition).
 *
 * Use `variant="staggerContainer"` on a parent and wrap each child in a
 * <Reveal> (or a motion element with one of the child variants) to get a
 * staggered cascade.
 */
export const Reveal = forwardRef<HTMLElement, RevealProps>(function Reveal(
  { as = 'div', delay = 0, variant = 'fadeUp', className, children },
  ref,
) {
  const reduced = useReducedMotion();
  const revealProps = useRevealProps(delay);
  const variants = revealVariants[variant];

  // Reduced motion: skip framer entirely, render the bare element.
  if (reduced) {
    return createElement(as, { ref, className }, children);
  }

  const MotionTag = motion(as as ElementType);

  return (
    <MotionTag
      ref={ref as Ref<HTMLElement>}
      className={className}
      variants={variants}
      {...revealProps}
    >
      {children}
    </MotionTag>
  );
});
